import { TableConfigV2, Column } from 'hooks/useMuiTableBuilderV2';
import merge from 'lodash/merge';
import { RealmTableView } from 'RealmModule/constants/realm';
import {
  SandboxTableHeadCellIds,
  realmIdCellProps,
  teamNameCellProps,
  statusCellProps,
  createdCellProps,
  creatorCellProps,
  tagsCellProps,
  archiverCellProps,
  archivedAtCellProps,
  realmsCellProps,
  tenantCellProps,
} from 'SandboxModule/components/sharedTableConfigs';
import { HeaderCell } from 'components/Cells/HeaderCell';
import { createRealmDataCell } from './utils';

export const COLUMNS = {
  CHECKBOX: SandboxTableHeadCellIds.Checkbox,
  TENANT: SandboxTableHeadCellIds.Tenant,
  REALM_ID: SandboxTableHeadCellIds.RealmId,
  TEAM_NAME: SandboxTableHeadCellIds.TeamName,
  CREATOR: SandboxTableHeadCellIds.Creator,
  STATUS: SandboxTableHeadCellIds.Status,
  CREATED_AT: SandboxTableHeadCellIds.Created,
  TAGS: SandboxTableHeadCellIds.Tags,
  REALM_SERVICES: SandboxTableHeadCellIds.Services,
  ARCHIVER: SandboxTableHeadCellIds.Archiver,
  ARCHIVED_AT: SandboxTableHeadCellIds.ArchivedAt,
};

export const ROW_TYPES = {
  HEADER: 'HEADER',
  DATA: 'DATA',
};

// Define base column objects to carry proper labels and sort flags
const baseColumns = {
  CHECKBOX: { id: COLUMNS.CHECKBOX, label: '', sortDisabled: true } as Column,
  TENANT: { id: COLUMNS.TENANT, label: tenantCellProps.label, sortDisabled: true } as Column,
  REALM_ID: { id: COLUMNS.REALM_ID, label: realmIdCellProps.label } as Column,
  TEAM_NAME: { id: COLUMNS.TEAM_NAME, label: teamNameCellProps.label } as Column,
  CREATOR: { id: COLUMNS.CREATOR, label: creatorCellProps.label } as Column,
  STATUS: { id: COLUMNS.STATUS, label: statusCellProps.label, sortDisabled: true } as Column,
  CREATED_AT: { id: COLUMNS.CREATED_AT, label: createdCellProps.label } as Column,
  TAGS: { id: COLUMNS.TAGS, label: tagsCellProps.label, sortDisabled: true } as Column,
  REALM_SERVICES: {
    id: COLUMNS.REALM_SERVICES,
    label: realmsCellProps.label,
    sortDisabled: true,
  } as Column,
  ARCHIVER: { id: COLUMNS.ARCHIVER, label: archiverCellProps.label, sortDisabled: true } as Column,
  ARCHIVED_AT: { id: COLUMNS.ARCHIVED_AT, label: archivedAtCellProps.label } as Column,
};

const sharedConfig: Partial<TableConfigV2> = {
  tableHeaderTitle: 'Realms',
  rowTypes: ROW_TYPES,
  hideToolbar: true,
  cellsByRowTypesAndColumns: {
    [ROW_TYPES.HEADER]: {
      [COLUMNS.CHECKBOX]: HeaderCell,
      [COLUMNS.TENANT]: HeaderCell,
      [COLUMNS.REALM_ID]: HeaderCell,
      [COLUMNS.TEAM_NAME]: HeaderCell,
      [COLUMNS.CREATOR]: HeaderCell,
      [COLUMNS.STATUS]: HeaderCell,
      [COLUMNS.CREATED_AT]: HeaderCell,
      [COLUMNS.TAGS]: HeaderCell,
      [COLUMNS.REALM_SERVICES]: HeaderCell,
      [COLUMNS.ARCHIVER]: HeaderCell,
      [COLUMNS.ARCHIVED_AT]: HeaderCell,
    },
    [ROW_TYPES.DATA]: {
      [COLUMNS.CHECKBOX]: createRealmDataCell(SandboxTableHeadCellIds.Checkbox),
      [COLUMNS.TENANT]: createRealmDataCell(SandboxTableHeadCellIds.Tenant),
      [COLUMNS.REALM_ID]: createRealmDataCell(SandboxTableHeadCellIds.RealmId),
      [COLUMNS.TEAM_NAME]: createRealmDataCell(SandboxTableHeadCellIds.TeamName),
      [COLUMNS.CREATOR]: createRealmDataCell(SandboxTableHeadCellIds.Creator),
      [COLUMNS.STATUS]: createRealmDataCell(SandboxTableHeadCellIds.Status),
      [COLUMNS.CREATED_AT]: createRealmDataCell(SandboxTableHeadCellIds.Created),
      [COLUMNS.TAGS]: createRealmDataCell(SandboxTableHeadCellIds.Tags),
      [COLUMNS.REALM_SERVICES]: createRealmDataCell(SandboxTableHeadCellIds.Services),
      [COLUMNS.ARCHIVER]: createRealmDataCell(SandboxTableHeadCellIds.Archiver),
      [COLUMNS.ARCHIVED_AT]: createRealmDataCell(SandboxTableHeadCellIds.ArchivedAt),
    },
  },
};

const customConfigs = {
  [RealmTableView.Active]: {
    columns: [
      baseColumns.CHECKBOX,
      baseColumns.TENANT,
      baseColumns.REALM_ID,
      baseColumns.TEAM_NAME,
      baseColumns.STATUS,
      baseColumns.CREATOR,
      baseColumns.CREATED_AT,
      baseColumns.TAGS,
      baseColumns.REALM_SERVICES,
    ],
    columnsMinWidth: {
      [COLUMNS.CHECKBOX]: 40,
    },
  },
  [RealmTableView.Archived]: {
    tableHeaderTitle: 'Archived Realms',
    columns: [
      baseColumns.CHECKBOX,
      baseColumns.TENANT,
      baseColumns.REALM_ID,
      baseColumns.TEAM_NAME,
      baseColumns.STATUS,
      baseColumns.CREATOR,
      baseColumns.CREATED_AT,
      baseColumns.ARCHIVER,
      baseColumns.ARCHIVED_AT,
      baseColumns.TAGS,
      baseColumns.REALM_SERVICES,
    ],
    columnsMinWidth: {
      [COLUMNS.CHECKBOX]: 40,
    },
  },
};

export const getRealmTableV2Config = ({ variant }: { variant: RealmTableView }): TableConfigV2 => {
  return merge({} as TableConfigV2, sharedConfig, customConfigs[variant]);
};
